import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Autocomplete,
  Box,
  Button,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import api from "../../services/api";

/**
 * PaquetesDataTable - Tabla editable para gestionar paquetes en evento
 * Permite agregar, editar y eliminar paquetes
 */
const PaquetesDataTable = ({ value = [], onChange, error, helperText }) => {
  const [paquetes, setPaquetes] = useState([]);
  const [paquetesDisponibles, setPaquetesDisponibles] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cargar paquetes disponibles
  useEffect(() => {
    loadPaquetes();
  }, []);

  // Actualizar cuando cambia value (prop)
  useEffect(() => {
    if (Array.isArray(value)) {
      setPaquetes(value);
    }
  }, [value]);

  const loadPaquetes = async () => {
    try {
      setLoading(true);
      console.log("[PaquetesDataTable] Cargando paquetes...");
      const response = await api.get("/paquetes");
      console.log("[PaquetesDataTable] Response:", response.data);
      // paqueteService normaliza la respuesta en el interceptor
      // response.data debería ser { paquetes: [...] }
      const list =
        response.data?.paquetes ||
        (Array.isArray(response.data) ? response.data : []);
      console.log("[PaquetesDataTable] Lista final:", list);
      setPaquetesDisponibles(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("[PaquetesDataTable] Error cargando paquetes:", error);
      setPaquetesDisponibles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaquete = () => {
    const newPaquete = {
      paquete_id: null,
      cantidad: 1,
      precio_unitario: 0,
      total: 0,
    };
    const updatedPaquetes = [...paquetes, newPaquete];
    setPaquetes(updatedPaquetes);
    onChange(updatedPaquetes);
    setEditingIndex(paquetes.length);
  };

  const handleDeletePaquete = (index) => {
    const updatedPaquetes = paquetes.filter((_, i) => i !== index);
    setPaquetes(updatedPaquetes);
    onChange(updatedPaquetes);
  };

  const handleCellChange = (index, field, value) => {
    const updatedPaquetes = [...paquetes];
    updatedPaquetes[index][field] = value;

    // Calcular total automáticamente
    if (field === "cantidad" || field === "precio_unitario") {
      updatedPaquetes[index].total =
        (updatedPaquetes[index].cantidad || 0) *
        (updatedPaquetes[index].precio_unitario || 0);
    }

    // Si cambia el paquete, actualizar precio
    if (field === "paquete_id" && value) {
      const paqueteSeleccionado = paquetesDisponibles.find(
        (p) => p.id === value,
      );
      if (paqueteSeleccionado) {
        updatedPaquetes[index].precio_unitario =
          paqueteSeleccionado.precio || 0;
        updatedPaquetes[index].total =
          (updatedPaquetes[index].cantidad || 1) *
          (paqueteSeleccionado.precio || 0);
      }
    }

    setPaquetes(updatedPaquetes);
    onChange(updatedPaquetes);
  };

  const handleSelectPaquete = (index, paqueteId) => {
    handleCellChange(index, "paquete_id", paqueteId);
    setEditingIndex(null);
  };

  const getTotalPaquetes = () => {
    return paquetes.reduce((total, p) => total + (p.total || 0), 0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(value || 0);
  };

  const getNombrePaquete = (paqueteId) => {
    const paquete = paquetesDisponibles.find((p) => p.id === paqueteId);
    return paquete?.nombre || "Seleccionar paquete";
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 800, mx: "auto" }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 600, color: "#CE93D8" }}
      >
        Paquetes
      </Typography>

      <TableContainer
        component={Paper}
        sx={{
          mb: 2,
          boxShadow: "0 4px 20px rgba(156, 39, 176, 0.15)",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#131829",
          border: "1px solid rgba(156, 39, 176, 0.2)",
        }}
      >
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead
            sx={{
              background: "linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)",
              "& .MuiTableCell-head": {
                background: "transparent",
                color: "#FFFFFF",
                fontWeight: "700",
                fontSize: "0.9rem",
                padding: "16px 12px",
                borderBottom: "2px solid rgba(156, 39, 176, 0.3)",
              },
            }}
          >
            <TableRow sx={{ height: 56 }}>
              <TableCell
                sx={{
                  fontWeight: "700",
                  color: "white",
                  fontSize: "0.9rem",
                  padding: "16px 12px",
                }}
              >
                Paquete
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: "700",
                  color: "white",
                  fontSize: "0.9rem",
                  padding: "16px 12px",
                }}
              >
                Cantidad
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: "700",
                  color: "white",
                  fontSize: "0.9rem",
                  padding: "16px 12px",
                }}
              >
                Precio Unitario
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: "700",
                  color: "white",
                  fontSize: "0.9rem",
                  padding: "16px 12px",
                }}
              >
                Total
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "700",
                  color: "white",
                  fontSize: "0.9rem",
                  padding: "16px 12px",
                }}
              >
                ⚙️
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paquetes.map((paquete, index) => (
              <TableRow
                key={`paquete-${index}`}
                sx={{
                  "&:hover": {
                    bgcolor: "rgba(156, 39, 176, 0.08)",
                  },
                  bgcolor:
                    editingIndex === index
                      ? "rgba(156, 39, 176, 0.12)"
                      : "#1a1f36",
                  transition: "background-color 0.2s ease",
                }}
              >
                {/* Paquete */}
                <TableCell>
                  {editingIndex === index ? (
                    <Autocomplete
                      size="small"
                      options={paquetesDisponibles}
                      getOptionLabel={(option) => option?.nombre || ""}
                      value={
                        paquetesDisponibles.find(
                          (p) => p.id === paquete.paquete_id,
                        ) || null
                      }
                      onChange={(e, newValue) =>
                        handleSelectPaquete(index, newValue?.id || null)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Buscar paquete..."
                          error={!paquete.paquete_id}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "#0a0e27",
                              color: "#FFFFFF",
                              "& fieldset": {
                                borderColor: !paquete.paquete_id
                                  ? "#FF5252"
                                  : "rgba(156, 39, 176, 0.3)",
                              },
                              "&:hover fieldset": {
                                borderColor: "#CE93D8",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#CE93D8",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "#FFFFFF",
                            },
                            "& .MuiInputBase-input::placeholder": {
                              color: "#B0BEC5",
                              opacity: 1,
                            },
                          }}
                        />
                      )}
                      loading={loading}
                      sx={{
                        minWidth: 200,
                        "& .MuiAutocomplete-popupIndicator": {
                          color: "#B0BEC5",
                        },
                        "& .MuiAutocomplete-clearIndicator": {
                          color: "#B0BEC5",
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        p: 1,
                        borderRadius: 1,
                        "&:hover": {
                          bgcolor: "rgba(156, 39, 176, 0.1)",
                        },
                      }}
                      onClick={() => setEditingIndex(index)}
                    >
                      <span style={{ color: "#FFFFFF" }}>
                        {getNombrePaquete(paquete.paquete_id)}
                      </span>
                      <EditIcon fontSize="small" sx={{ color: "#CE93D8" }} />
                    </Box>
                  )}
                </TableCell>

                {/* Cantidad */}
                <TableCell align="right">
                  <TextField
                    type="number"
                    size="small"
                    value={paquete.cantidad || 1}
                    onChange={(e) =>
                      handleCellChange(
                        index,
                        "cantidad",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    sx={{
                      maxWidth: 80,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#0a0e27",
                        color: "#FFFFFF",
                        "& fieldset": {
                          borderColor: "rgba(156, 39, 176, 0.3)",
                        },
                        "&:hover fieldset": { borderColor: "#CE93D8" },
                        "&.Mui-focused fieldset": { borderColor: "#CE93D8" },
                      },
                      "& .MuiInputBase-input": {
                        color: "#FFFFFF",
                        textAlign: "center",
                      },
                    }}
                    inputProps={{ min: 1, max: 9999 }}
                  />
                </TableCell>

                {/* Precio Unitario */}
                <TableCell align="right">
                  <TextField
                    type="number"
                    size="small"
                    value={paquete.precio_unitario || 0}
                    onChange={(e) =>
                      handleCellChange(
                        index,
                        "precio_unitario",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    sx={{
                      maxWidth: 100,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#0a0e27",
                        color: "#FFFFFF",
                        "& fieldset": {
                          borderColor: "rgba(156, 39, 176, 0.3)",
                        },
                        "&:hover fieldset": { borderColor: "#CE93D8" },
                        "&.Mui-focused fieldset": { borderColor: "#CE93D8" },
                        "&.Mui-disabled": { bgcolor: "#1a1f36" },
                      },
                      "& .MuiInputBase-input": {
                        color: "#FFFFFF",
                        textAlign: "right",
                      },
                      "& .Mui-disabled .MuiInputBase-input": {
                        color: "#B0BEC5",
                        WebkitTextFillColor: "#B0BEC5",
                      },
                    }}
                    inputProps={{ step: 0.01, min: 0 }}
                    disabled={paquete.paquete_id !== null}
                  />
                </TableCell>

                {/* Total */}
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", color: "#92FE9D" }}
                >
                  {formatCurrency(paquete.total || 0)}
                </TableCell>

                {/* Acciones */}
                <TableCell align="center">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeletePaquete(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {paquetes.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{ py: 3, bgcolor: "#1a1f36" }}
                >
                  <Typography sx={{ color: "#B0BEC5" }}>
                    {loading
                      ? "Cargando paquetes..."
                      : 'No hay paquetes agregados. Haz clic en "Agregar Paquete"'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {paquetesDisponibles.length === 0 &&
              paquetes.length === 0 &&
              !loading && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 3, bgcolor: "rgba(255, 193, 7, 0.1)" }}
                  >
                    <Typography sx={{ color: "#FFC107" }} variant="body2">
                      ⚠️ No hay paquetes disponibles en la base de datos
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Botón Agregar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={handleAddPaquete}
          disabled={paquetesDisponibles.length === 0}
          sx={{
            background: "linear-gradient(45deg, #6a1b9a 30%, #8e24aa 90%)",
            boxShadow: "0 3px 5px 2px rgba(106, 27, 154, .3)",
            borderRadius: 1,
          }}
        >
          + Agregar Paquete
        </Button>

        <Box
          sx={{
            textAlign: "right",
            background:
              "linear-gradient(135deg, rgba(106, 27, 154, 0.3) 0%, rgba(142, 36, 170, 0.3) 100%)",
            border: "1px solid rgba(156, 39, 176, 0.2)",
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#B0BEC5" }}>
            Total Paquetes:
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#CE93D8", fontSize: "1.3rem" }}
          >
            {formatCurrency(getTotalPaquetes())}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Typography color="error" variant="caption">
          {helperText || "Error en paquetes"}
        </Typography>
      )}
    </Box>
  );
};

export default PaquetesDataTable;
